import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash, Plugs, Plus, X, Globe } from '@phosphor-icons/react';
import { CopyApiButton } from '../components/CopyApiButton';

const HostingerAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ label: '', api_token: '' });

  useEffect(() => { fetchAccounts(); }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hostinger/accounts`, { withCredentials: true });
      setAccounts(res.data);
    } catch (e) {
      toast.error('Failed to fetch accounts');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/hostinger/accounts`, form, { withCredentials: true });
      toast.success('Hostinger account added successfully');
      setShowAdd(false);
      setForm({ label: '', api_token: '' });
      fetchAccounts();
    } catch (e) {
      toast.error('Failed to add Hostinger account');
    }
  };

  const removeAccount = async (id) => {
    if (!window.confirm('Are you sure you want to remove this account?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/hostinger/accounts/${id}`, { withCredentials: true });
      fetchAccounts();
      toast.success('Account removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove account');
    }
  };

  const testConnection = async (id) => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/hostinger/accounts/${id}/test`, {}, { withCredentials: true });
      toast.success(`Connection OK! ${res.data.domain_count} domains found.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Connection failed: invalid token or permissions');
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-black text-[#00E5FF] mb-4">HOSTINGER ACCOUNTS</h1>
          <div className="bg-[#0A0B0E] border border-[#00E5FF]/30 p-6 max-w-3xl space-y-4 text-sm text-slate-300 font-mono">
            <h3 className="text-[#00E5FF] font-bold font-sans text-lg uppercase tracking-wider mb-2">How to Generate a Hostinger API Token</h3>
            <div className="space-y-1">
              <p><strong className="text-white">Step 1:</strong> Log into hPanel and open <a href="https://hpanel.hostinger.com/profile/api" target="_blank" rel="noreferrer" className="text-[#00E5FF] hover:underline">Account &gt; API</a>.</p>
              <p><strong className="text-white">Step 2:</strong> Create a new API token (it is shown only once — copy it).</p>
              <p><strong className="text-white">Step 3:</strong> Paste it below. No IP whitelisting needed — auth is token-based.</p>
            </div>
          </div>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-hostinger-account-btn"
          className="flex items-center gap-2 bg-[#00E5FF] text-black px-4 py-2 font-bold hover:bg-[#00B8CC] transition-colors shrink-0">
          <Plus size={20} /> Add Account
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/20 p-8 w-full max-w-lg relative animate-in zoom-in-95 duration-200">
            <button onClick={() => setShowAdd(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={24} /></button>
            <h2 className="text-2xl font-bold mb-6 text-[#00E5FF]">LINK HOSTINGER ACCOUNT</h2>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">Label / Nickname</label>
                <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="hostinger-label-input" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#00E5FF]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">API Token</label>
                <input required type="password" value={form.api_token} onChange={(e) => setForm({ ...form, api_token: e.target.value })} data-testid="hostinger-token-input" className="w-full bg-[#0A0B0E] border border-white/10 p-2 mt-1 focus:outline-none focus:border-[#00E5FF] font-mono text-sm" />
              </div>
              <button type="submit" data-testid="hostinger-submit-btn" className="w-full bg-[#00E5FF] text-black font-bold py-3 mt-4 hover:bg-[#00B8CC] transition-colors uppercase tracking-wider">
                Link Account
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {accounts.map(acc => (
          <div key={acc.id} className="border border-[#00E5FF]/20 bg-card p-6 relative group flex flex-col gap-4">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <Globe size={24} className="text-[#00E5FF]" />
                <h3 className="text-xl font-bold">{acc.label}</h3>
              </div>
              <div className="flex gap-2">
                <button onClick={() => testConnection(acc.id)} className="text-slate-400 hover:text-[#00E5FF] transition-colors p-1" title="Test Connection"><Plugs size={20} /></button>
                <CopyApiButton provider="hostinger" accountId={acc.id} />
                <button onClick={() => removeAccount(acc.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" title="Remove Account"><Trash size={20} /></button>
              </div>
            </div>
            <div className="space-y-2 text-sm font-mono text-slate-400 border-t border-white/10 pt-4 mt-auto">
              <p>API Token: <span className="text-white">{acc.api_token}</span></p>
            </div>
          </div>
        ))}
        {accounts.length === 0 && (
          <div className="col-span-full p-8 border border-dashed border-white/20 text-center text-slate-500">
            No Hostinger accounts linked.
          </div>
        )}
      </div>
    </div>
  );
};

export default HostingerAccounts;
